import { cs } from '../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import api from '../../services/api';
import MonthlyRevenueTrendChart from '../../components/admin/MonthlyRevenueTrendChart';
import {
  Users,
  GraduationCap,
  Trophy,
  Calendar,
  CalendarDays,
  ClipboardList,
  DollarSign,
  TrendingUp,
  TrendingDown,
  UserCheck,
  Star,
  CreditCard,
  Repeat,
  PieChart,
  Grid3X3,
  Clock,
  Settings,
  ChevronDown,
  ChevronUp,
  Pause,
  ArrowRightLeft,
  MessageSquare,
  PlayCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Bell,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import UniformCampaignBanner from '../../components/UniformCampaignBanner';
import EventNotificationBanner from '../../components/EventNotificationBanner';
import { usePullToRefresh, RefreshIndicator } from '../../hooks/usePullToRefresh';
import { useFeatures } from '../../context/FeatureContext';
import { SectionOrderList } from '../../components/admin/dashboard/SectionOrderList';

// Default dashboard cards configuration
const DEFAULT_CARDS = {
  stats_row_1: {
    enabled: true,
    cards: ["total_players", "total_parents", "total_coaches", "programs"]
  },
  stats_row_2: {
    enabled: true,
    cards: ["active_classes", "trial_requests", "pending_enrollments", "active_enrollments"]
  },
  lane_booking_overview: { enabled: true },
  today_bookings: { enabled: true },
  upcoming_games: { enabled: true },
  unreconciled_payments: { enabled: true },
  revenue_overview: { enabled: true },
  // iter274: chart is now its own toggle so existing admins whose saved
  // prefs already disable `revenue_overview` still see the trend chart.
  // Defaults to enabled and the `?.enabled !== false` gate below treats
  // missing keys as enabled.
  monthly_revenue_chart: { enabled: true },
  pending_trial_requests: { enabled: true, collapsed: false },
  recent_enrollments: { enabled: true, collapsed: false },
  quick_actions: { enabled: true }
};

const CARD_LABELS = {
  lane_booking_overview: "Lane Booking Overview",
  today_bookings: "Today's Bookings",
  upcoming_games: "Upcoming Games",
  unreconciled_payments: "Unreconciled Stripe Payments",
  revenue_overview: "Revenue Overview",
  monthly_revenue_chart: "Monthly Revenue Trend Chart",
  pending_trial_requests: "Pending Trial Requests",
  recent_enrollments: "Recent Enrollment Requests",
  quick_actions: "Quick Actions"
};

const StatCard = ({ title, value, icon: Icon, color, href, subtitle, urgent }) => {
  const content = (
    <Card className={`card-hover border ${urgent ? 'border-red-300 bg-red-50/50' : 'border-slate-200'} hover:border-slate-300 h-full`}>
      <CardContent className="p-3 lg:p-6 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="flex flex-col justify-between min-h-[56px] lg:min-h-[72px]">
            <p className={`text-xs lg:text-sm font-medium ${urgent ? 'text-red-600' : 'text-muted-foreground'}`}>{title}</p>
            <p className={`font-heading text-2xl lg:text-3xl font-bold ${urgent ? 'text-red-700' : 'text-card-foreground'} mt-0.5 lg:mt-1`}>{value}</p>
            <p className={`text-[10px] lg:text-xs ${urgent ? 'text-red-500 font-medium' : 'text-muted-foreground'} mt-0.5 lg:mt-1 h-3 lg:h-4`}>{subtitle || '\u00A0'}</p>
          </div>
          <div className={`p-2 lg:p-3 rounded-lg ${color} self-center`}>
            <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (href) {
    return <Link to={href} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>{content}</Link>;
  }
  return content;
};

const RevenueCard = ({ title, value, icon: Icon, color, change, changeLabel }) => (
  <Card className="border border-slate-200">
    <CardContent className="p-3 lg:p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs lg:text-sm font-medium text-muted-foreground">{title}</p>
          <p className="font-heading text-xl lg:text-2xl font-bold text-card-foreground mt-0.5 lg:mt-1">{value}</p>
          {change !== undefined && (
            <div className="flex items-center gap-1 mt-0.5 lg:mt-1">
              {change >= 0 ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={`text-[10px] lg:text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? '+' : ''}{change}%
              </span>
              {changeLabel && <span className="text-[10px] lg:text-xs text-muted-foreground hidden sm:inline">{changeLabel}</span>}
            </div>
          )}
        </div>
        <div className={`p-2 lg:p-3 rounded-lg ${color}`}>
          <Icon className="h-4 w-4 lg:h-5 lg:w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// iter335d — extracted to module scope so it isn't re-created on every
// Dashboard render (react/no-unstable-nested-components).
const HEALTH_DOT = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500' };
const HealthBadge = ({ bucket, health }) => {
  if (!health || health.overdue === 0) return null;
  const staleDays = health.oldest
    ? Math.max(0, Math.floor((Date.now() - new Date(health.oldest).getTime()) / 86400000))
    : null;
  return (
    <p
      className="mt-1 text-[10px] font-medium text-red-600 flex items-center gap-1"
      data-testid={`bucket-overdue-${bucket}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${HEALTH_DOT[health.health]}`} />
      {health.overdue} overdue{staleDays !== null && staleDays > 0 ? ` · ${staleDays}d stale` : ''}
    </p>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ 
  title, 
  icon: Icon, 
  iconColor, 
  count, 
  children, 
  collapsed, 
  onToggle, 
  borderColor = "border-slate-200",
  bgColor = "",
  viewAllLink,
  viewAllLabel = "View All"
}) => (
  <Card className={`border-2 ${borderColor} ${bgColor}`}>
    <CardHeader 
      className={`border-b ${borderColor.replace('border-', 'border-')}/50 cursor-pointer p-3 lg:p-6`}
      onClick={onToggle}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 lg:gap-3">
          <CardTitle className="font-heading text-base lg:text-xl flex items-center gap-1.5 lg:gap-2">
            <Icon className={`h-4 w-4 lg:h-5 lg:w-5 ${iconColor}`} />
            {title}
          </CardTitle>
          {count > 0 && (
            <Badge className={`${iconColor.replace('text-', 'bg-').replace('-500', '-100').replace('-600', '-100')} ${iconColor} border-0 text-[10px] lg:text-xs`}>
              {count}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5 lg:gap-2">
          {viewAllLink && (
            <Link 
              to={viewAllLink} 
              className={`text-xs lg:text-sm ${iconColor} hover:underline font-medium`}
              onClick={(e) => e.stopPropagation()}
            >
              {viewAllLabel}
            </Link>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 lg:h-8 lg:w-8 p-0">
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </CardHeader>
    {!collapsed && (
      <CardContent className="p-0">
        {children}
      </CardContent>
    )}
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  // iter292: per-category health snapshot — merged into the existing
  // Revenue by Category cards as a small overdue/staleness badge so we
  // don't introduce a duplicate widget.
  const [categoryHealth, setCategoryHealth] = useState(null);
  const [recentEnrollments, setRecentEnrollments] = useState([]);
  const [allPendingEnrollments, setAllPendingEnrollments] = useState([]);
  const [recentTrials, setRecentTrials] = useState([]);
  const [allPendingTrials, setAllPendingTrials] = useState([]);
  const [pendingSubRequests, setPendingSubRequests] = useState(0);
  const [pendingMovementRequests, setPendingMovementRequests] = useState(0);
  const [pendingScheduleRequests, setPendingScheduleRequests] = useState(0);
  const [pendingWithdrawalRequests, setPendingWithdrawalRequests] = useState(0);
  const [unreadCoachNotes, setUnreadCoachNotes] = useState(0);
  const [upcomingEnrollmentChanges, setUpcomingEnrollmentChanges] = useState(null);
  const [todayBookings, setTodayBookings] = useState({ total: 0, items: [] });
  const [upcomingGames, setUpcomingGames] = useState({ total: 0, items: [] });
  const [unreconciled, setUnreconciled] = useState({ unreconciled_count: 0, items: [], stripe_mode: 'unknown' });
  const [reconcilingPi, setReconcilingPi] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dashboard preferences
  const [cardPrefs, setCardPrefs] = useState(DEFAULT_CARDS);
  // iter335c — lane-only tenants (no enrollments.core) should not see the
  // enrollment/roster-oriented dashboard sections.
  const { isPlanFeatureEnabled } = useFeatures();
  const showAcademyStats = isPlanFeatureEnabled ? isPlanFeatureEnabled('enrollments.core') : true;
  // iter335d — per-stream dashboard gating so a tenant only sees widgets
  // for revenue streams it's entitled to (matches BillingOverviewTab).
  const gate = (k) => (isPlanFeatureEnabled ? isPlanFeatureEnabled(k) : true);
  const hasLanes = gate('bookings.lanes');
  const hasBookings = gate('bookings.lanes') || gate('bookings.small_groups') || gate('bookings.coaching');
  const hasGames = gate('tournaments.games');
  const hasTournaments = gate('tournaments.registrations');
  const hasCamps = gate('camps.core');
  const hasCoaching = gate('bookings.small_groups') || gate('bookings.coaching');
  // Which customizable sections this academy's plan can show at all.
  const SECTION_AVAILABLE = {
    lane_booking_overview: hasLanes,
    today_bookings: hasBookings,
    upcoming_games: hasGames,
    unreconciled_payments: gate('payments.stripe_byok'),
    revenue_overview: gate('payments.module'),
    monthly_revenue_chart: gate('payments.module'),
    pending_trial_requests: showAcademyStats,
    recent_enrollments: showAcademyStats,
    quick_actions: true,
  };
  const quickActions = [
    gate('programs.catalog') && { to: '/admin/programs', tid: 'quick-add-program', icon: Trophy, title: 'Add New Program', sub: 'Create training programs', bg: 'bg-blue-100 group-hover:bg-blue-500', fg: 'text-blue-600' },
    gate('programs.classes') && { to: '/admin/classes', tid: 'quick-add-class', icon: Calendar, title: 'Schedule Class', sub: 'Add new class sessions', bg: 'bg-blue-100 group-hover:bg-blue-500', fg: 'text-blue-600' },
    gate('programs.coaches') && { to: '/admin/coaches', tid: 'quick-add-coach', icon: GraduationCap, title: 'Add Coach', sub: 'Onboard new coaches', bg: 'bg-purple-100 group-hover:bg-purple-500', fg: 'text-purple-600' },
    hasLanes && { to: '/admin/lanes', tid: 'quick-lane-bookings', icon: Calendar, title: 'Lane Bookings', sub: 'Book or manage lanes', bg: 'bg-teal-100 group-hover:bg-teal-500', fg: 'text-teal-600' },
    hasLanes && { to: '/admin/all-bookings', tid: 'quick-all-bookings', icon: Trophy, title: 'All Bookings', sub: 'Everything on the lanes', bg: 'bg-teal-100 group-hover:bg-teal-500', fg: 'text-teal-600' },
  ].filter(Boolean);
  const [sectionOrderList, setSectionOrderList] = useState(Object.keys(CARD_LABELS));
  const [tempOrder, setTempOrder] = useState(Object.keys(CARD_LABELS));
  const sectionOrder = (key) => {
    const keys = key === 'today_bookings' ? ['today_bookings', 'upcoming_games'] : [key];
    return 1 + Math.min(...keys.map((k) => { const i = sectionOrderList.indexOf(k); return i < 0 ? 99 : i; }));
  };
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [tempPrefs, setTempPrefs] = useState(DEFAULT_CARDS);
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Collapsible state
  const [trialsCollapsed, setTrialsCollapsed] = useState(false);
  const [enrollmentsCollapsed, setEnrollmentsCollapsed] = useState(false);

  // iter409 — every panel loads on its own; only the (fast) layout prefs gate the first paint.
  const load = (url, onOk, fallback) => api.get(url).then((r) => onOk(r.data ?? fallback)).catch(() => onOk(fallback));
  // Plan-gated panels: re-run when the plan finishes loading (it can arrive after mount, e.g. right after login).
  const loadGated = () => {
    if (showAcademyStats) {
      load('/enrollments?status=pending', (d) => setAllPendingEnrollments(d || []), []);
      load('/enrollments?limit=5', (d) => setRecentEnrollments(d || []), []);
      load('/trial-requests?status=pending', (d) => { setAllPendingTrials(d || []); setRecentTrials((d || []).slice(0, 5)); }, []);
    }
    if (hasTournaments) load('/tournaments/withdrawal-requests/count', (d) => setPendingWithdrawalRequests(d?.count || 0), { count: 0 });
    if (hasBookings) load('/dashboard/today-bookings', setTodayBookings, { total: 0, items: [] });
    if (hasGames) load('/dashboard/upcoming-games', setUpcomingGames, { total: 0, items: [] });
  };
  const gatedMounted = useRef(false);
  useEffect(() => {
    if (!gatedMounted.current) { gatedMounted.current = true; return; }
    loadGated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAcademyStats, hasTournaments, hasBookings, hasGames]);
  const fetchData = async () => {
    const prefsReq = api.get('/auth/me/dashboard-preferences').catch(() => ({ data: { cards: DEFAULT_CARDS } }));
    load('/dashboard/stats', setStats, {});
    loadGated();
    load('/admin/revenue/stats', setRevenueStats, null);
    load('/admin/enrollment-requests?status=pending', (d) => setPendingSubRequests(d?.length || 0), []);
    load('/movement-requests?status=pending', (d) => setPendingMovementRequests(d?.length || 0), []);
    load('/coach-notes?is_read=false', (d) => setUnreadCoachNotes(d?.length || 0), []);
    load('/class-schedule-requests/pending/count', (d) => setPendingScheduleRequests(d?.count || 0), { count: 0 });
    load('/admin/jobs/enrollment-changes/upcoming?days_ahead=7', setUpcomingEnrollmentChanges, { upcoming_resumes: [], upcoming_cancellations: [], total_upcoming: 0 });
    load('/admin/payments/unreconciled?days=30&limit=10', setUnreconciled, { unreconciled_count: 0, items: [], stripe_mode: 'unknown' });
    load('/admin/billing/category-health', (d) => setCategoryHealth(d?.categories || []), { categories: [] });

    try {
      const prefsRes = await prefsReq;
      // iter274: merge saved prefs over defaults so newly-added keys appear for existing admins.
      const savedPrefs = prefsRes.data?.cards || {};
      const prefs = { ...DEFAULT_CARDS, ...savedPrefs };
      setCardPrefs(prefs);
      setTempPrefs(prefs);
      const savedOrder = Array.isArray(prefsRes.data?.order) ? prefsRes.data.order : [];
      const fullOrder = [...savedOrder.filter((k) => CARD_LABELS[k]), ...Object.keys(CARD_LABELS).filter((k) => !savedOrder.includes(k))];
      setSectionOrderList(fullOrder);
      setTempOrder(fullOrder);
      setTrialsCollapsed(prefs.pending_trial_requests?.collapsed ?? false);
      setEnrollmentsCollapsed(prefs.recent_enrollments?.collapsed ?? false);
    } finally {
      setLoading(false);
    }
  };

  const { refreshing, onTouchStart, onTouchEnd } = usePullToRefresh(fetchData);

  // ---- Unreconciled Stripe payments ----
  // Refetch only the card (cheaper than the whole dashboard) after a
  // successful per-row reconcile so the row drops off immediately.
  const refetchUnreconciled = async () => {
    try {
      const r = await api.get('/admin/payments/unreconciled?days=30&limit=10&fresh=true');
      setUnreconciled(r.data || { unreconciled_count: 0, items: [], stripe_mode: 'unknown' });
    } catch (e) {
      // silent
    }
  };

  const handleReconcilePI = async (piId) => {
    setReconcilingPi(piId);
    try {
      const res = await api.post('/admin/payments/reconcile-by-pi', { payment_intent_id: piId });
      const updated = res.data?.updated || [];
      if (updated.length === 0) {
        toast.warning(`No record matched ${piId}. Open the full reconcile dialog for details.`);
      } else {
        toast.success(`Marked ${updated.length} record(s) paid for ${piId}.`);
      }
      await refetchUnreconciled();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Reconcile failed');
      toast.error(msg);
    } finally {
      setReconcilingPi(null);
    }
  };

  const handleDismissPI = async (piId) => {
    const reason = window.prompt(
      `Dismiss ${piId}?\n\nUse this for charges made directly in the Stripe Dashboard (not through this app), or PIs you want to stop seeing in this card.\n\nOptional reason:`,
      ''
    );
    if (reason === null) return; // user cancelled
    try {
      await api.post('/admin/payments/dismiss-unreconciled', {
        payment_intent_id: piId,
        reason: reason || 'External charge',
      });
      toast.success(`Dismissed ${piId}`);
      await refetchUnreconciled();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Dismiss failed');
    }
  };

  // ---- Backfill Missing Amounts ----
  // Recovery tool for production rows marked "paid" with a blank amount
  // (bug window before Feb 27, 2026 when reconcile-by-pi didn't write the
  // amount). One click runs a dry-run first, shows the preview, then asks
  // for confirmation before committing.
  const [showBackfillDialog, setShowBackfillDialog] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);

  const handleBackfillPreview = async () => {
    setBackfillLoading(true);
    try {
      const r = await api.post('/admin/payments/backfill-missing-amounts?dry_run=true');
      setBackfillPreview(r.data || null);
      setShowBackfillDialog(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill preview failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleBackfillCommit = async () => {
    setBackfillLoading(true);
    try {
      const r = await api.post('/admin/payments/backfill-missing-amounts?dry_run=false');
      const d = r.data || {};
      const backfilled = d.total_backfilled || 0;
      const captured = (d.total_captured || 0).toFixed(2);
      const failures = d.failures || [];
      const stripeMode = d.stripe_mode;

      if (backfilled === 0) {
        // Don't fake a green success when nothing actually changed —
        // surface the actual failure reasons. The most common cause in
        // production is a Stripe API-key mode mismatch (test PIs being
        // looked up via a live key or vice versa).
        const reasonSummary = failures.length > 0
          ? failures.slice(0, 3).map(f => f.reason).join(' · ')
          : 'No qualifying rows.';
        toast.warning(
          `Backfilled 0 of ${failures.length} row(s). ${reasonSummary}`
            + (stripeMode ? ` (Stripe mode: ${stripeMode})` : ''),
          { duration: 10000 },
        );
      } else {
        toast.success(
          `Backfilled ${backfilled} row(s) · ${cs()}${captured} captured`
            + (failures.length > 0 ? ` · ${failures.length} skipped` : ''),
        );
      }
      setShowBackfillDialog(false);
      setBackfillPreview(null);
      await refetchUnreconciled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSavePreferences = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/auth/me/dashboard-preferences', { cards: tempPrefs, order: tempOrder });
      setCardPrefs(tempPrefs);
      setSectionOrderList(tempOrder);
      setShowSettingsModal(false);
      toast.success('Dashboard preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleResetPreferences = async () => {
    setSavingPrefs(true);
    try {
      await api.delete('/auth/me/dashboard-preferences');
      setCardPrefs(DEFAULT_CARDS);
      setTempPrefs(DEFAULT_CARDS);
      setSectionOrderList(Object.keys(CARD_LABELS));
      setTempOrder(Object.keys(CARD_LABELS));
      setTrialsCollapsed(false);
      setEnrollmentsCollapsed(false);
      setShowSettingsModal(false);
      toast.success('Dashboard reset to defaults');
    } catch (error) {
      toast.error('Failed to reset preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const toggleCardPref = (cardKey) => {
    setTempPrefs(prev => ({
      ...prev,
      [cardKey]: {
        ...prev[cardKey],
        enabled: !prev[cardKey]?.enabled
      }
    }));
  };

  // Save collapse state when toggling
  const handleTrialsToggle = async () => {
    const newState = !trialsCollapsed;
    setTrialsCollapsed(newState);
    
    // Update preferences in background
    const updatedPrefs = {
      ...cardPrefs,
      pending_trial_requests: { ...cardPrefs.pending_trial_requests, collapsed: newState }
    };
    setCardPrefs(updatedPrefs);
    try {
      await api.put('/auth/me/dashboard-preferences', { cards: updatedPrefs, order: sectionOrderList });
    } catch (e) {
      // Silently fail - not critical
    }
  };

  const handleEnrollmentsToggle = async () => {
    const newState = !enrollmentsCollapsed;
    setEnrollmentsCollapsed(newState);
    
    const updatedPrefs = {
      ...cardPrefs,
      recent_enrollments: { ...cardPrefs.recent_enrollments, collapsed: newState }
    };
    setCardPrefs(updatedPrefs);
    try {
      await api.put('/auth/me/dashboard-preferences', { cards: updatedPrefs, order: sectionOrderList });
    } catch (e) {
      // Silently fail
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const pendingTrialsCount = allPendingTrials.length;
  const pendingEnrollmentsCount = allPendingEnrollments.length;

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="flex flex-col gap-5 lg:gap-8" data-testid="admin-dashboard" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <RefreshIndicator refreshing={refreshing} />
        {/* Banners for admins who have children */}
        <UniformCampaignBanner />
        <EventNotificationBanner />
        
        {/* Header with Settings */}
        <div className="space-y-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Dashboard Overview</h1>
            <p className="text-xs lg:text-sm text-slate-500">Welcome back! Here&apos;s what&apos;s happening.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              setTempPrefs(cardPrefs);
              setShowSettingsModal(true);
            }}
            className="flex items-center gap-1.5 lg:gap-2 text-xs lg:text-sm"
            data-testid="dashboard-settings-btn"
          >
            <Settings className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            <span className="hidden sm:inline">Customize Dashboard</span>
            <span className="sm:hidden">Settings</span>
          </Button>
          </div>
        </div>

        {/* Upcoming Enrollment Changes Notification Banner */}
        {upcomingEnrollmentChanges && upcomingEnrollmentChanges.total_upcoming > 0 && (
          <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="enrollment-changes-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-500 rounded-lg shrink-0">
                  <Bell className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-amber-900">Upcoming Enrollment Changes</h3>
                    <Badge className="bg-amber-200 text-amber-800 border-amber-300">
                      {upcomingEnrollmentChanges.total_upcoming} in next 7 days
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Upcoming Resumes */}
                    {upcomingEnrollmentChanges.upcoming_resumes?.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                          <PlayCircle className="h-4 w-4" />
                          Paused Enrollments Resuming ({upcomingEnrollmentChanges.upcoming_resumes.length})
                        </div>
                        <div className="space-y-1">
                          {upcomingEnrollmentChanges.upcoming_resumes.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="text-sm flex items-center gap-2 pl-6">
                              <span className={`font-medium ${item.is_today ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {item.player_name}
                              </span>
                              <span className="text-slate-500">→</span>
                              <span className="text-slate-600">{item.program_name}</span>
                              <Badge variant="outline" className={`text-xs ${item.is_today ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-slate-100 text-slate-600'}`}>
                                {item.is_today ? 'Today' : item.days_until === 1 ? 'Tomorrow' : `in ${item.days_until} days`}
                              </Badge>
                            </div>
                          ))}
                          {upcomingEnrollmentChanges.upcoming_resumes.length > 3 && (
                            <Link to="/admin/enrollments?tab=paused" className="text-xs text-emerald-600 hover:underline pl-6">
                              +{upcomingEnrollmentChanges.upcoming_resumes.length - 3} more...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Upcoming Cancellations */}
                    {upcomingEnrollmentChanges.upcoming_cancellations?.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm font-medium text-rose-700">
                          <XCircle className="h-4 w-4" />
                          Scheduled Cancellations ({upcomingEnrollmentChanges.upcoming_cancellations.length})
                        </div>
                        <div className="space-y-1">
                          {upcomingEnrollmentChanges.upcoming_cancellations.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="text-sm flex items-center gap-2 pl-6">
                              <span className={`font-medium ${item.is_today ? 'text-rose-700' : 'text-slate-700'}`}>
                                {item.player_name}
                              </span>
                              <span className="text-slate-500">→</span>
                              <span className="text-slate-600">{item.program_name}</span>
                              <Badge variant="outline" className={`text-xs ${item.is_today ? 'bg-rose-100 text-rose-700 border-rose-300' : 'bg-slate-100 text-slate-600'}`}>
                                {item.is_today ? 'Today' : item.days_until === 1 ? 'Tomorrow' : `in ${item.days_until} days`}
                              </Badge>
                            </div>
                          ))}
                          {upcomingEnrollmentChanges.upcoming_cancellations.length > 3 && (
                            <Link to="/admin/enrollments" className="text-xs text-rose-600 hover:underline pl-6">
                              +{upcomingEnrollmentChanges.upcoming_cancellations.length - 3} more...
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Today's changes highlight */}
                  {(upcomingEnrollmentChanges.resumes_today > 0 || upcomingEnrollmentChanges.cancellations_today > 0) && (
                    <div className="mt-3 pt-3 border-t border-amber-200 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">
                        Today: {upcomingEnrollmentChanges.resumes_today > 0 && `${upcomingEnrollmentChanges.resumes_today} resume(s)`}
                        {upcomingEnrollmentChanges.resumes_today > 0 && upcomingEnrollmentChanges.cancellations_today > 0 && ', '}
                        {upcomingEnrollmentChanges.cancellations_today > 0 && `${upcomingEnrollmentChanges.cancellations_today} cancellation(s)`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actionable Cards Row - Items requiring attention */}
        {showAcademyStats && (
        <div className="space-y-2">
          <h2 className="text-xs lg:text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
            <ClipboardList className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
            Requires Attention
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 lg:gap-6">
            <StatCard 
              title="Trial Requests" 
              value={pendingTrialsCount} 
              icon={Star} 
              color={pendingTrialsCount > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/trials"
              subtitle={pendingTrialsCount > 0 ? "⚠ Pending review" : "All caught up"}
              urgent={pendingTrialsCount > 0}
            />
            <StatCard 
              title="Pending Enrollments" 
              value={pendingEnrollmentsCount} 
              icon={ClipboardList} 
              color={pendingEnrollmentsCount > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/enrollments?tab=pending"
              subtitle={pendingEnrollmentsCount > 0 ? "⚠ Needs attention" : "All processed"}
              urgent={pendingEnrollmentsCount > 0}
            />
            <StatCard 
              title="Movement Requests" 
              value={pendingMovementRequests} 
              icon={ArrowRightLeft} 
              color={pendingMovementRequests > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/movement-requests"
              subtitle={pendingMovementRequests > 0 ? "⚠ Coach requests" : "None pending"}
              urgent={pendingMovementRequests > 0}
            />
            <StatCard 
              title="Schedule Requests" 
              value={pendingScheduleRequests} 
              icon={CalendarDays} 
              color={pendingScheduleRequests > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/schedule-requests"
              subtitle={pendingScheduleRequests > 0 ? "⚠ Parent requests" : "None pending"}
              urgent={pendingScheduleRequests > 0}
            />
            <StatCard 
              title="Subscription Requests" 
              value={pendingSubRequests} 
              icon={Pause} 
              color={pendingSubRequests > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/enrollment-requests"
              subtitle={pendingSubRequests > 0 ? "⚠ Pending approval" : "All processed"}
              urgent={pendingSubRequests > 0}
            />
            <StatCard 
              title="Coach Notes" 
              value={unreadCoachNotes} 
              icon={MessageSquare} 
              color={unreadCoachNotes > 0 ? "bg-red-500" : "bg-slate-400"}
              href="/admin/billing?tab=notes"
              subtitle={unreadCoachNotes > 0 ? "Unread notes" : "All read"}
              urgent={unreadCoachNotes > 0}
            />
            <StatCard 
              title="Withdrawals" 
              value={pendingWithdrawalRequests} 
              icon={XCircle} 
              color={pendingWithdrawalRequests > 0 ? "bg-red-600" : "bg-slate-400"}
              href="/admin/withdrawal-requests"
              subtitle={pendingWithdrawalRequests > 0 ? "Pending review" : "None pending"}
              urgent={pendingWithdrawalRequests > 0}
            />
          </div>
        </div>
        )}

        {/* Informative Stats Row */}
        {showAcademyStats && cardPrefs.stats_row_1?.enabled !== false && (
          <div className="space-y-2">
            <h2 className="text-xs lg:text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <PieChart className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
              Academy Overview
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 lg:gap-4">
              <StatCard 
                title="Total Players" 
                value={stats ? (stats.total_players || 0) : '…'} 
                icon={Users} 
                color="bg-blue-500"
                href="/admin/players"
              />
              <StatCard 
                title="Total Parents" 
                value={stats ? (stats.total_parents || 0) : '…'} 
                icon={UserCheck} 
                color="bg-blue-500"
                href="/admin/parents"
              />
              <StatCard 
                title="Active Coaches" 
                value={stats ? (stats.active_coaches || 0) : '…'} 
                icon={GraduationCap} 
                color="bg-purple-500"
                href="/admin/coaches"
              />
              <StatCard 
                title="Programs" 
                value={stats ? (stats.total_programs || 0) : '…'} 
                icon={Trophy} 
                color="bg-amber-500"
                href="/admin/programs"
              />
              <StatCard 
                title="Active Classes" 
                value={stats ? (stats.total_classes || 0) : '…'} 
                icon={Calendar} 
                color="bg-indigo-500"
                href="/admin/classes"
              />
              <StatCard 
                title="Active Enrollments" 
                value={stats ? (stats.active_enrollments || 0) : '…'} 
                icon={ClipboardList} 
                color="bg-green-500"
                href="/admin/enrollments"
              />
            </div>
          </div>
        )}

        {/* Today's Bookings + Upcoming Games — quick links */}
        {((cardPrefs.today_bookings?.enabled !== false && hasBookings) || (cardPrefs.upcoming_games?.enabled !== false && hasGames)) && (
          <div style={{ order: sectionOrder('today_bookings') }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {cardPrefs.today_bookings?.enabled !== false && hasBookings && (
              <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white" data-testid="card-today-bookings">
                <CardHeader className="border-b border-blue-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-blue-600" />
                      Today&apos;s Bookings
                      {todayBookings.date && (
                        <span className="text-xs font-normal text-slate-500" data-testid="today-booking-date">
                          {(() => {
                            const [y, m, d] = todayBookings.date.split('-').map(Number);
                            const dateObj = new Date(y, m - 1, d);
                            return dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                          })()}
                        </span>
                      )}
                      <Badge variant="outline" className="ml-1 text-blue-700 border-blue-300">{todayBookings.total}</Badge>
                    </CardTitle>
                    <Link
                      to="/admin/lanes"
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      data-testid="view-all-today-bookings"
                    >
                      View Calendar →
                    </Link>
                  </div>
                </CardHeader>
                <CardContent className="p-3 lg:p-5">
                  {todayBookings.items.length > 0 ? (
                    <ul className="divide-y divide-slate-100">
                      {todayBookings.items.map((b) => (
                        <li key={b.id} className="py-2 flex items-center justify-between gap-3" data-testid={`today-booking-${b.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-sm font-mono text-slate-600 shrink-0 w-12">{b.start_time}</span>
                            <span className="text-sm font-medium text-slate-800 truncate">{b.customer_name}</span>
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-300 shrink-0">{b.lane_name}</Badge>
                          </div>
                          <Badge className={`text-[10px] shrink-0 ${
                            b.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                            b.payment_status === 'refunded' ? 'bg-violet-100 text-violet-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {b.payment_status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 py-4 text-center">No bookings on the schedule today.</p>
                  )}
                  {todayBookings.total > todayBookings.items.length && (
                    <p className="text-xs text-slate-500 mt-2">Showing first {todayBookings.items.length} of {todayBookings.total} — open the calendar for the full day.</p>
                  )}
                </CardContent>
              </Card>
            )}

            {cardPrefs.upcoming_games?.enabled !== false && hasGames && (
              <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="card-upcoming-games">
                <CardHeader className="border-b border-amber-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-xl flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-600" />
                      Upcoming Games
                      <Badge variant="outline" className="ml-1 text-amber-700 border-amber-300">{upcomingGames.total}</Badge>
                    </CardTitle>
                    <Link
                      to="/admin/tournaments"
                      className="text-sm text-amber-700 hover:text-amber-800 font-medium"
                      data-testid="view-all-upcoming-games"
                    >
                      All Tournaments →
                    </Link>
                  </div>
                </CardHeader>
                <CardContent className="p-3 lg:p-5">
                  {upcomingGames.items.length > 0 ? (
                    <ul className="divide-y divide-slate-100">
                      {upcomingGames.items.map((g) => (
                        <li key={g.id} className="py-2" data-testid={`upcoming-game-${g.id}`}>
                          <Link
                            to={`/admin/tournaments/${g.tournament_id}`}
                            className="flex items-center justify-between gap-3 group"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-sm font-mono text-slate-600 shrink-0 w-24">
                                {g.game_date}{g.start_time ? ` ${g.start_time}` : ''}
                              </span>
                              <span className="text-sm font-medium text-slate-800 truncate group-hover:text-amber-700">
                                {g.tournament_name}
                              </span>
                              {g.age_category && (
                                <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-300 shrink-0">
                                  {g.age_category}
                                </Badge>
                              )}
                            </div>
                            {g.opponent_team && (
                              <span className="text-xs text-slate-500 shrink-0 truncate max-w-[160px]">
                                vs {g.opponent_team}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500 py-4 text-center">No games scheduled in the upcoming window.</p>
                  )}
                  {upcomingGames.total > upcomingGames.items.length && (
                    <p className="text-xs text-slate-500 mt-2">Showing first {upcomingGames.items.length} of {upcomingGames.total} — view tournament for full schedule.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          </div>
        )}

        {/* Unreconciled Stripe Payments — succeeded PIs in Stripe with no
            matching paid record in our DB. Helps catch missed webhooks /
            ACH that settled days after the parent's checkout. */}
        {cardPrefs.unreconciled_payments?.enabled !== false && gate('payments.stripe_byok') && (
          <div style={{ order: sectionOrder('unreconciled_payments') }}>
          <Card className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-white" data-testid="card-unreconciled-payments">
            <CardHeader className="border-b border-rose-100">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-rose-600" />
                  Unreconciled Stripe Payments
                  <Badge variant="outline" className="ml-1 text-rose-700 border-rose-300" data-testid="unreconciled-count">
                    {unreconciled.unreconciled_count || 0}
                  </Badge>
                  {unreconciled.stripe_mode && unreconciled.stripe_mode !== 'live' && (
                    <Badge className="ml-1 bg-amber-100 text-amber-700 border-0 text-[10px]">
                      Stripe: {unreconciled.stripe_mode}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {RETIRED_TOOLS_VISIBLE && (<Button
                    variant="outline"
                    size="sm"
                    onClick={handleBackfillPreview}
                    disabled={backfillLoading}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="backfill-missing-amounts-btn"
                  >
                    {backfillLoading ? (
                      <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    )}
                    Backfill Missing Amounts
                  </Button>)}
                  <Link
                    to="/admin/billing-management?reconcile=1"
                    className="text-sm text-rose-600 hover:text-rose-700 font-medium"
                    data-testid="open-reconcile-dialog"
                  >
                    Open Full Reconcile →
                  </Link>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 lg:p-5">
              {(unreconciled.items || []).length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {unreconciled.items.map((p) => {
                    const isReconciling = reconcilingPi === p.payment_intent_id;
                    const dateStr = p.created_iso
                      ? new Date(p.created_iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '';
                    return (
                      <li
                        key={p.payment_intent_id}
                        className="py-2.5 flex items-center justify-between gap-3"
                        data-testid={`unreconciled-row-${p.payment_intent_id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <span className="text-sm font-mono text-slate-600 shrink-0 w-16">{dateStr}</span>
                          <span className="text-sm font-semibold text-slate-900 shrink-0 w-20">
                            {cs()}{p.amount?.toFixed(2)}
                          </span>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-slate-800 truncate">
                              {p.customer_email || <span className="text-slate-400 italic">No email</span>}
                            </span>
                            <span className="text-[11px] text-slate-500 font-mono truncate">{p.payment_intent_id}</span>
                            {p.description && (
                              <span className="text-[11px] text-slate-500 truncate" title={p.description}>
                                {p.description}
                              </span>
                            )}
                            {p.linked_record && (
                              <span className="text-[10px] text-amber-700">
                                Linked {p.linked_record.entity_type} (status: {p.linked_record.payment_status || p.linked_record.status || 'unknown'})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {RETIRED_TOOLS_VISIBLE && (<Button
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-700 hover:bg-rose-100"
                            onClick={() => handleReconcilePI(p.payment_intent_id)}
                            disabled={isReconciling}
                            data-testid={`reconcile-btn-${p.payment_intent_id}`}
                          >
                            {isReconciling ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                                Reconcile
                              </>
                            )}
                          </Button>)}
                          {RETIRED_TOOLS_VISIBLE && (<Button
                            size="sm"
                            variant="ghost"
                            className="text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                            onClick={() => handleDismissPI(p.payment_intent_id)}
                            title="Dismiss — not our charge (external Stripe charge)"
                            data-testid={`dismiss-btn-${p.payment_intent_id}`}
                          >
                            Dismiss
                          </Button>)}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 py-4 text-center" data-testid="unreconciled-empty">
                  All Stripe payments in the last 30 days are reconciled. Nice.
                </p>
              )}
              {unreconciled.scanned > 0 && (
                <p className="text-[11px] text-slate-400 mt-3 text-center">
                  Scanned {unreconciled.scanned} succeeded PI(s) over the last {unreconciled.window_days || 30} days · {unreconciled.already_paid_in_db || 0} already settled in DB
                </p>
              )}
            </CardContent>
          </Card>
          </div>
        )}
        {cardPrefs.lane_booking_overview?.enabled !== false && hasLanes && (
          <div style={{ order: sectionOrder('lane_booking_overview') }}>
          <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white" data-testid="card-lane-booking-overview">
            <CardHeader className="border-b border-teal-100">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Grid3X3 className="h-5 w-5 text-teal-600" />
                  Lane Booking Overview
                </CardTitle>
                <Link 
                  to="/admin/lanes" 
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                  data-testid="view-lane-management"
                >
                  Manage Lanes →
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-3 lg:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:gap-4">
                <div className="bg-white rounded-lg p-2.5 lg:p-4 border border-teal-200">
                  <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-teal-600" />
                    <p className="text-[10px] lg:text-xs text-slate-500 uppercase tracking-wide">Revenue</p>
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-teal-700" data-testid="lane-revenue-month">
                    {cs()}{stats ? (stats.lane_revenue_month || 0).toLocaleString() : '…'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2.5 lg:p-4 border border-slate-200">
                  <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                    <Calendar className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-blue-600" />
                    <p className="text-[10px] lg:text-xs text-slate-500 uppercase tracking-wide">Today</p>
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-slate-900" data-testid="lane-bookings-today">
                    {stats ? (stats.today_lane_bookings || 0) : '…'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2.5 lg:p-4 border border-slate-200">
                  <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                    <CreditCard className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-amber-600" />
                    <p className="text-[10px] lg:text-xs text-slate-500 uppercase tracking-wide">Pending</p>
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-amber-600" data-testid="lane-pending-payments">
                    {stats ? (stats.pending_lane_payments || 0) : '…'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2.5 lg:p-4 border border-slate-200">
                  <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                    <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-purple-600" />
                    <p className="text-[10px] lg:text-xs text-slate-500 uppercase tracking-wide">Waitlist</p>
                  </div>
                  <p className="text-lg lg:text-2xl font-bold text-purple-600" data-testid="lane-waitlist-count">
                    {stats ? (stats.lane_waitlist_count || 0) : '…'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Revenue Section */}
        {cardPrefs.revenue_overview?.enabled !== false && gate('payments.module') && revenueStats && (
          <div style={{ order: sectionOrder('revenue_overview') }}>
          <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardHeader className="border-b border-emerald-100">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                  Revenue Overview
                </CardTitle>
                <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                  {revenueStats?.current_month || 'This Month'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-3 lg:p-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-4">
                <RevenueCard
                  title="Total Revenue"
                  value={`${cs()}${(revenueStats?.total_revenue || 0).toLocaleString()}`}
                  icon={DollarSign}
                  color="bg-emerald-600"
                />
                <RevenueCard
                  title={`Revenue (${revenueStats?.current_month?.split(' ')[0] || 'This Month'})`}
                  value={`${cs()}${(revenueStats?.revenue_this_month || 0).toLocaleString()}`}
                  icon={CreditCard}
                  color="bg-blue-600"
                  change={revenueStats?.month_over_month_change}
                  changeLabel="vs last month"
                />
                <RevenueCard
                  title="Monthly Recurring"
                  value={`${cs()}${(revenueStats?.monthly_recurring_revenue || 0).toLocaleString()}`}
                  icon={Repeat}
                  color="bg-purple-600"
                />
                <RevenueCard
                  title="Active Enrollments"
                  value={revenueStats?.active_subscriptions || revenueStats?.active_enrollments || 0}
                  icon={Users}
                  color="bg-indigo-600"
                />
              </div>

              {/* Revenue Breakdown */}
              {revenueStats?.breakdown && (() => {
                // iter292: derive per-bucket health by aggregating
                // PaymentCategory rows. Adding a new category to the
                // backend registry (`services/payment_categories.py`)
                // surfaces here automatically through its `revenue_bucket`.
                const bucketHealth = {};
                for (const c of categoryHealth || []) {
                  const b = c.revenue_bucket;
                  if (!bucketHealth[b]) {
                    bucketHealth[b] = { overdue: 0, unpaid: 0, oldest: null, health: 'green' };
                  }
                  bucketHealth[b].overdue += c.overdue_count || 0;
                  bucketHealth[b].unpaid += c.unpaid_count || 0;
                  // Worst grade wins: red > yellow > green.
                  const grades = ['green', 'yellow', 'red'];
                  if (grades.indexOf(c.health) > grades.indexOf(bucketHealth[b].health)) {
                    bucketHealth[b].health = c.health;
                  }
                  if (c.oldest_unpaid_due_date) {
                    if (!bucketHealth[b].oldest || c.oldest_unpaid_due_date < bucketHealth[b].oldest) {
                      bucketHealth[b].oldest = c.oldest_unpaid_due_date;
                    }
                  }
                }
                // HealthBadge + HEALTH_DOT are module-scoped (see top of file).
                return (                <div className="mt-6 pt-6 border-t border-emerald-100">
                  <h4 className="text-sm font-medium text-slate-600 mb-4 flex items-center gap-2">
                    <PieChart className="h-4 w-4" />
                    Revenue by Category (All Time)
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
                    {showAcademyStats && (
                    <Link to="/admin/billing-management?section=subscriptions" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Subscriptions</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.subscriptions || 0).toLocaleString()}</p>
                        <HealthBadge bucket="subscriptions" health={bucketHealth.subscriptions} />
                        <p className="text-xs text-blue-600 mt-1">View details →</p>
                      </div>
                    </Link>
                    )}
                    {hasCoaching && (
                    <Link to="/admin/billing-management?section=coaching" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-purple-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Coaching</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.coaching || 0).toLocaleString()}</p>
                        <HealthBadge bucket="coaching" health={bucketHealth.coaching} />
                        <p className="text-xs text-purple-600 mt-1">View details →</p>
                      </div>
                    </Link>
                    )}
                    {hasTournaments && (
                    <Link to="/admin/billing-management?section=tournaments" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Tournaments</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.tournaments || 0).toLocaleString()}</p>
                        <HealthBadge bucket="tournaments" health={bucketHealth.tournaments} />
                        <p className="text-xs text-amber-600 mt-1">View details →</p>
                      </div>
                    </Link>
                    )}
                    {hasLanes && (
                    <Link to="/admin/billing-management?section=lanes" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-teal-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Lane Bookings</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.lane_bookings || 0).toLocaleString()}</p>
                        <HealthBadge bucket="lane_bookings" health={bucketHealth.lane_bookings} />
                        <p className="text-xs text-teal-600 mt-1">View details →</p>
                      </div>
                    </Link>
                    )}
                    {hasCamps && (
                    <Link to="/admin/billing-management?section=camps" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-pink-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Camps</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.camps || 0).toLocaleString()}</p>
                        <HealthBadge bucket="camps" health={bucketHealth.camps} />
                        <p className="text-xs text-pink-600 mt-1">View details →</p>
                      </div>
                    </Link>
                    )}
                    <Link to="/admin/billing-management?section=misc" className="block">
                      <div className="bg-white rounded-lg p-4 border border-slate-200 hover:border-slate-400 hover:shadow-md transition-all cursor-pointer">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Other</p>
                        <p className="text-lg font-bold text-slate-900">{cs()}{(revenueStats.breakdown.other || 0).toLocaleString()}</p>
                        <HealthBadge bucket="other" health={bucketHealth.other} />
                        <p className="text-xs text-slate-600 mt-1">View details →</p>
                      </div>
                    </Link>
                  </div>

                  {/* Gross/Net reconciliation row — explains how
                      revenue_this_month is computed and matches the
                      Billing Management overview exactly. */}
                  {revenueStats?.gross_revenue_this_month !== undefined && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm" data-testid="revenue-reconciliation-row">
                      <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-emerald-700">Gross (this month)</p>
                        <p className="text-base font-semibold text-emerald-900">{cs()}{revenueStats.gross_revenue_this_month.toLocaleString()}</p>
                      </div>
                      <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-rose-700">Refunded</p>
                        <p className="text-base font-semibold text-rose-900">−{cs()}{(revenueStats.refunded_this_month || 0).toLocaleString()}</p>
                      </div>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-xs uppercase tracking-wide text-slate-600">Net = shown above</p>
                        <p className="text-base font-semibold text-slate-900">{cs()}{(revenueStats.revenue_this_month || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}
            </CardContent>
          </Card>
          </div>
        )}

        {/* Monthly Revenue Trend chart — same aggregator as the revenue
            card, so chart totals match the headline numbers exactly.
            iter274: gated by its own `monthly_revenue_chart` pref so it
            renders even when an admin has hidden the `revenue_overview`
            stat card. Missing key defaults to enabled. */}
        {cardPrefs.monthly_revenue_chart?.enabled !== false && gate('payments.module') && (
          <div style={{ order: sectionOrder('monthly_revenue_chart') }}>
          <MonthlyRevenueTrendChart />
          </div>
        )}

        {/* Pending Trial Requests - Collapsible */}
        {cardPrefs.pending_trial_requests?.enabled !== false && pendingTrialsCount > 0 && showAcademyStats && (
          <div style={{ order: sectionOrder('pending_trial_requests') }}>
          <CollapsibleSection
            title="Pending Trial Requests"
            icon={Star}
            iconColor="text-amber-500"
            count={pendingTrialsCount}
            collapsed={trialsCollapsed}
            onToggle={handleTrialsToggle}
            borderColor="border-amber-200"
            bgColor="bg-amber-50/50"
            viewAllLink="/admin/trials"
          >
            <div className="divide-y divide-amber-100">
              {recentTrials.map((trial) => (
                <div 
                  key={trial.id} 
                  className="p-4 flex items-center justify-between hover:bg-amber-50 transition-colors"
                  data-testid={`trial-${trial.id}`}
                >
                  <div>
                    <p className="font-medium text-slate-900">{trial.child_name}</p>
                    <p className="text-sm text-slate-500">
                      Age: {trial.age} • {trial.skill_level} • Parent: {trial.parent_name}
                    </p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                    Pending
                  </span>
                </div>
              ))}
            </div>
          </CollapsibleSection>
          </div>
        )}

        {/* Recent Enrollments - Collapsible */}
        {cardPrefs.recent_enrollments?.enabled !== false && showAcademyStats && (
          <div style={{ order: sectionOrder('recent_enrollments') }}>
          <CollapsibleSection
            title="Recent Enrollment Requests"
            icon={ClipboardList}
            iconColor="text-blue-600"
            count={pendingEnrollmentsCount}
            collapsed={enrollmentsCollapsed}
            onToggle={handleEnrollmentsToggle}
            borderColor="border-slate-200"
            viewAllLink="/admin/enrollments"
          >
            {recentEnrollments.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No enrollment requests yet
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {recentEnrollments.map((enrollment) => (
                  <div 
                    key={enrollment.id} 
                    className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    data-testid={`enrollment-${enrollment.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-900">{enrollment.player_name}</p>
                      <p className="text-sm text-slate-500">
                        {enrollment.program_name} • Parent: {enrollment.parent_name}
                      </p>
                    </div>
                    <span className={`
                      px-3 py-1 rounded-full text-xs font-medium capitalize
                      ${enrollment.status === 'pending' ? 'bg-amber-100 text-amber-700' : ''}
                      ${enrollment.status === 'approved' ? 'bg-green-100 text-green-700' : ''}
                      ${enrollment.status === 'rejected' ? 'bg-red-100 text-red-700' : ''}
                      ${enrollment.status === 'completed' ? 'bg-blue-100 text-blue-700' : ''}
                    `}>
                      {enrollment.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>
          </div>
        )}

        {/* Quick Actions */}
        {cardPrefs.quick_actions?.enabled !== false && quickActions.length > 0 && (
          <div style={{ order: sectionOrder('quick_actions') }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickActions.map((qa) => (
              <Link key={qa.to} to={qa.to} data-testid={qa.tid}>
                <Card className="card-hover border border-slate-200 cursor-pointer group">
                  <CardContent className="p-4 lg:p-6 flex items-center gap-3 lg:gap-4">
                    <div className={`p-3 rounded-lg ${qa.bg} transition-colors`}>
                      <qa.icon className={`h-6 w-6 ${qa.fg} group-hover:text-white transition-colors`} />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{qa.title}</p>
                      <p className="text-sm text-slate-500">{qa.sub}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          </div>
        )}
      </div>

      {/* Dashboard Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-slate-600" />
              Customize Dashboard
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Toggle sections on or off, and drag (or use the arrows) to change their order.
            </p>
            
            <SectionOrderList
              order={tempOrder.filter((k) => SECTION_AVAILABLE[k] !== false)}
              labels={CARD_LABELS}
              isOn={(k) => tempPrefs[k]?.enabled !== false}
              onToggle={toggleCardPref}
              onReorder={(visible) => setTempOrder([...visible, ...tempOrder.filter((k) => !visible.includes(k))])}
            />
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button 
              variant="outline" 
              onClick={handleResetPreferences}
              disabled={savingPrefs}
              className="text-slate-600"
            >
              Reset to Defaults
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowSettingsModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleSavePreferences} disabled={savingPrefs}>
                {savingPrefs ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backfill Missing Amounts — preview + confirm dialog */}
      <Dialog open={showBackfillDialog} onOpenChange={setShowBackfillDialog}>
        <DialogContent className="max-w-2xl" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-amber-600" />
              Backfill Missing Payment Amounts
            </DialogTitle>
            <DialogDescription>
              Scans every &quot;paid&quot; row whose amount is blank, looks up the linked Stripe PaymentIntent, and writes the captured amount. Safe to re-run.
            </DialogDescription>
          </DialogHeader>
          {backfillPreview ? (
            <div className="space-y-3">
              {backfillPreview.stripe_mode && (
                <div className="text-xs text-slate-500">
                  Stripe mode: <span className="font-semibold">{backfillPreview.stripe_mode}</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                {['lane_bookings', 'tournament_registrations', 'payment_transactions'].map((k) => {
                  const s = backfillPreview[k] || {};
                  const label = k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                  return (
                    <div key={k} className="rounded border border-slate-200 p-3" data-testid={`backfill-stat-${k}`}>
                      <div className="text-xs text-slate-500">{label}</div>
                      <div className="text-2xl font-bold text-slate-900">{s.backfilled || 0}</div>
                      <div className="text-xs text-emerald-700">{cs()}{(s.captured || 0).toFixed(2)} to capture</div>
                      {(s.skipped || 0) > 0 && (
                        <div className="text-xs text-amber-600">{s.skipped} skipped</div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                <span className="text-sm text-slate-600">Total rows to backfill</span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900" data-testid="backfill-total-count">
                    {backfillPreview.total_backfilled || 0}
                  </div>
                  <div className="text-xs text-emerald-700" data-testid="backfill-total-captured">
                    {cs()}{(backfillPreview.total_captured || 0).toFixed(2)}
                  </div>
                </div>
              </div>
              {backfillPreview.failures && backfillPreview.failures.length > 0 && (
                <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs max-h-32 overflow-auto">
                  <div className="font-semibold text-amber-800 mb-1">
                    {backfillPreview.failures.length} row(s) couldn&apos;t be recovered:
                  </div>
                  <ul className="space-y-0.5 text-amber-800">
                    {backfillPreview.failures.slice(0, 8).map((f, i) => (
                      <li key={i} className="truncate">
                        {f.entity_type} {f.entity_id?.slice(0, 8)}… · {f.reason}
                      </li>
                    ))}
                    {backfillPreview.failures.length > 8 && (
                      <li className="italic">+ {backfillPreview.failures.length - 8} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Loading preview…</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowBackfillDialog(false); setBackfillPreview(null); }}
              disabled={backfillLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBackfillCommit}
              disabled={backfillLoading || !backfillPreview || (backfillPreview?.total_backfilled || 0) === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-backfill-btn"
            >
              {backfillLoading ? (
                <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Running…</>
              ) : (
                <>Confirm & Write Amounts</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminDashboard;
